import { StringDataFlowType, IDataFlowDataType, IntegerDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { Transformation } from '../../../model/dataflow/transformation';
import { DataFlowColumn } from '../../../model/dataset/dataflowcolumn';
import { Metamodel } from 'app/model/base/model';
import { Owned } from 'app/model/base/named';
import {
    dataFlowPatternNameParameter, dataFlowPatternTypeParameter, dataFlowPatternStreamParameter, dataFlowPatternOriginParameter, generateGrammarForColumns,
    IDataFlowArguments, dataFlowPatternParameter, dataFlowPatternPositionParameter
} from 'app/model/dataflow/dataflow-expression-utilities';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { DataFlowParameter } from 'app/model/dataflow/dataflow-parameter';
import { OwnedSet } from 'shell-models-lib';
import { ColumnExpressionProcessor } from 'app/model/dataflow/column-expression-processor';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import { ExpressionBasedColumnPattern } from 'app/model/dataflow/expression-based.column.pattern';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { dataFlowHierarchyPathParameter, dataFlowPositionParameter } from 'worker/app-workers/shared/constants';

export class PatternExpressionColumnsUtils {

    public static createParameterColumn(name: string, transformation: Transformation, dataFlowColumnType: IDataFlowDataType = new StringDataFlowType()): DataFlowColumn {
        const column = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
        column.name = name;
        column.dataFlowColumnType = dataFlowColumnType;
        return column;
    }

    public static getAvailableArgumentsForTemplateCondition(dataflowParameters: OwnedSet<DataFlowParameter>, cacheSinks: OwnedSet<DataFlowSink>,
        ownerTransformation: Transformation, addWindowFunctions: boolean = false, addAggregateFunctions: boolean = false): IDataFlowArguments {
        const nameColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternNameParameter, ownerTransformation);
        const typeColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternTypeParameter, ownerTransformation);
        const streamColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternStreamParameter, ownerTransformation);
        const originColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternOriginParameter, ownerTransformation);
        const positionColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternPositionParameter, ownerTransformation, new IntegerDataFlowType());
        const inputSchemaArguments = [new ColumnReference(nameColumn), new ColumnReference(typeColumn),
        new ColumnReference(streamColumn), new ColumnReference(originColumn), new ColumnReference(positionColumn)];

        return generateGrammarForColumns(inputSchemaArguments, cacheSinks, dataflowParameters,
            null, null, addWindowFunctions, addAggregateFunctions, false, true);
    }

    public static getAvailableArgumentsForColumnNameExpression(dataflowParameters: OwnedSet<DataFlowParameter>, cacheSinks: OwnedSet<DataFlowSink>,
        transformation: Transformation, addWindowFunctions: boolean = false, addAggregateFunctions: boolean = false, regexString: string = ''): IDataFlowArguments {

        const dataFlowPatternParameterColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternParameter, transformation);

        const inputSchemaArguments = [new ColumnReference(dataFlowPatternParameterColumn, undefined, undefined, true),
        ...this._getMetaParameterArguments(transformation, regexString)];

        return generateGrammarForColumns(inputSchemaArguments, cacheSinks, dataflowParameters, null, null, addWindowFunctions, addAggregateFunctions, false);
    }

    public static getAvailableArgumentsForColumnValueExpression(columnPattern: ExpressionBasedColumnPattern, dataflowParameters: OwnedSet<DataFlowParameter>, cacheSinks: OwnedSet<DataFlowSink>,
        transformation: Transformation, addWindowFunctions: boolean = false, addAggregateFunctions: boolean = false, regexString: string = ''): IDataFlowArguments {
        const dataFlowPatternParameterType = DataFlowTypesUtils.createType(columnPattern.basedColumnDataType ?
            columnPattern.basedColumnDataType.typeString : 'any');
        const dataFlowPatternParameterColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPatternParameter, transformation, dataFlowPatternParameterType);

        const distinctInputColumnRefs = [...transformation.distinctInputColumnRefs, new ColumnReference(dataFlowPatternParameterColumn, undefined, undefined, true),
        ...this._getMetaParameterArguments(transformation, regexString)];

        return generateGrammarForColumns(distinctInputColumnRefs, cacheSinks, dataflowParameters, null, null,
            addWindowFunctions, addAggregateFunctions, false, false, transformation.transientColumnRefs);
    }

    public static createCaptureGroupInputColumnRefs(transformation: Transformation, regexConditionExpressionStr: string): ColumnReference[] {
        const maxNumberOfCaptureGroups = Math.max(...transformation.distinctInputColumnRefs.map((inputColumn) =>
            ColumnExpressionProcessor.getCaptureGroupsFromRegexCondition(regexConditionExpressionStr, inputColumn.originalName).size));
        const captureGroupInputColumnRefs = [];
        for (let i = 1; i <= maxNumberOfCaptureGroups; i++) {
            const captureGroupColumn = PatternExpressionColumnsUtils.createParameterColumn(`$${i}`, transformation);
            captureGroupInputColumnRefs.push(new ColumnReference(captureGroupColumn, undefined, undefined, true));
        }
        return captureGroupInputColumnRefs;
    }

    private static _getMetaParameterArguments(transformation: Transformation, regexString?: string): ColumnReference[] {
        const metaParamArguments: ColumnReference[] = [];

        const dataFlowHierarchicalPathParameterColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowHierarchyPathParameter, transformation);
        const dataFlowPositionParameterColumn = PatternExpressionColumnsUtils.createParameterColumn(dataFlowPositionParameter, transformation, new IntegerDataFlowType());

        metaParamArguments.push(new ColumnReference(dataFlowHierarchicalPathParameterColumn, undefined, undefined, true));
        metaParamArguments.push(new ColumnReference(dataFlowPositionParameterColumn, undefined, undefined, true));

        if (regexString) {
            metaParamArguments.push(...PatternExpressionColumnsUtils.createCaptureGroupInputColumnRefs(transformation, regexString));
        }
        return metaParamArguments;
    }
}
